
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { forgotPassword } from '../services/authService';
import styles from '../styles/SignIn.module.css';
import headerStyles from '../styles/modules/Header.module.css';

const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
    const [signupUserType, setSignupUserType] = useState<'staff' | 'employer'>('staff');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showSignupPassword, setShowSignupPassword] = useState(false);
    const [policyModal, setPolicyModal] = useState<'terms' | 'privacy' | null>(null);
    const [policyContent, setPolicyContent] = useState('');
    const [policyLoading, setPolicyLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMessage, setForgotMessage] = useState('');
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        username: '',
        fullName: '',
        businessName: '',
        role: '',
        phoneNumber: '',
        placeOfResidence: '',
        acceptedTerms: false
    });

    // Restaurant roles for staff members
    const staffRoles = [
        'Barman',
        'Bartender',
        'Waiter',
        'Waitress',
        'Chef',
        'Sous Chef',
        'Cook',
        'Dishwasher',
        'Host/Hostess',
        'Sommelier',
        'Busser',
        'Kitchen Porter',
        'Line Cook',
        'Pastry Chef',
        'Counter Worker',
        'Pizza Maker',
        'Grill Master',
        'Baker',
        'Confectioner',
        'Hostess'
    ];

    // Roles for employers
    const employerRoles = [
        'Restaurant Owner',
        'Restaurant Manager',
        'General Manager',
        'Bar Manager',
        'Kitchen Manager',
        'Operations Manager',
        'F&B Manager',
        'Admin'
    ];

    const roles = activeTab === 'signup' ? (signupUserType === 'staff' ? staffRoles : employerRoles) : [];

    const isLoginFormValid = Boolean(formData.email.trim() && formData.password);
    const isSignupFormValid = Boolean(
        formData.email.trim() &&
        formData.password &&
        formData.fullName.trim() &&
        formData.role &&
        formData.phoneNumber.trim() &&
        formData.placeOfResidence.trim() &&
        formData.acceptedTerms &&
        (signupUserType === 'staff' || formData.businessName.trim())
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const createFallbackUsername = () => {
        const source = formData.email || formData.fullName;
        const normalized = source
            .toLowerCase()
            .replace(/@.*$/, '')
            .replace(/[^a-z0-9]+/g, '')
            .slice(0, 20);

        return normalized || `user${Date.now()}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (activeTab === 'signup') {
                // Validate required fields for sign up
                if (!formData.fullName || !formData.role || !formData.phoneNumber || !formData.placeOfResidence.trim()) {
                    setError(t('auth.fillAllFields', { defaultValue: 'Please fill in all required fields' }));
                    setLoading(false);
                    return;
                }

                // Validate employer-only required fields
                if (signupUserType === 'employer' && !formData.businessName.trim()) {
                    setError(t('auth.businessNameRequired', { defaultValue: 'Business name is required' }));
                    setLoading(false);
                    return;
                }

                // Validate terms and conditions
                if (!formData.acceptedTerms) {
                    setError(t('auth.mustAcceptTerms'));
                    setLoading(false);
                    return;
                }

                // Prepare signup data
                const signupData: any = {
                    username: formData.username || createFallbackUsername(),
                    fullName: formData.fullName,
                    email: formData.email,
                    password: formData.password,
                    role: formData.role,
                    userType: signupUserType,
                    phone: formData.phoneNumber,
                    placeOfResidence: formData.placeOfResidence.trim(),
                    acceptedTerms: Boolean(formData.acceptedTerms),
                };

                if (signupUserType === 'employer') {
                    signupData.businessName = formData.businessName.trim();
                }

                // Sign up
                console.log('Attempting signup to:', getApiUrl('auth/signup'));
                const response = await fetch(getApiUrl('auth/signup'), {
                    method: 'POST',
                    mode: 'cors',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(signupData),
                });

                console.log('Signup response status:', response.status);

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Sign up failed');
                }

                console.log('Sign up response:', data);

                // Store the token (if returned)
                if (data.token || data.access_token) {
                    localStorage.setItem('authToken', data.token || data.access_token);
                }

                // Store user data
                let userData: any = null;
                if (data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                    userData = data.user;
                } else if (data.username) {
                    // If user data is in the root of response
                    localStorage.setItem('user', JSON.stringify(data));
                    userData = data;
                }

                // Check if user is admin or superadmin
                const adminRoles = ['admin', 'superadmin'];
                const isAdmin = userData && adminRoles.some(role => 
                    userData.role.toLowerCase().includes(role.toLowerCase())
                );

                console.log('Sign up successful, navigating to', isAdmin ? 'admin' : 'dashboard');
                
                // Use window.location to ensure navigation happens
                window.location.href = isAdmin ? '/admin' : '/dashboard';
                return;
            }

            // Sign in
            console.log('Attempting login to:', getApiUrl('auth/login'));
            const response = await fetch(getApiUrl('auth/login'), {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password,
                }),
            });

            console.log('Response status:', response.status);
            console.log('Response headers:', Object.fromEntries(response.headers.entries()));

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Login failed');
            }

            console.log('Login response:', data);

            // Store the token (if returned)
            if (data.token || data.access_token) {
                localStorage.setItem('authToken', data.token || data.access_token);
            }

            // Store user data
            let userData: any = null;
            if (data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                userData = data.user;
            } else if (data.username) {
                // If user data is in the root of response
                localStorage.setItem('user', JSON.stringify(data));
                userData = data;
            }

            // Check if user is admin or superadmin
            const adminRoles = ['admin', 'superadmin'];
            const isAdmin = userData && adminRoles.some(role => 
                userData.role.toLowerCase().includes(role.toLowerCase())
            );

            console.log('Login successful, navigating to', isAdmin ? 'admin' : 'dashboard');
            
            // Use window.location to ensure navigation happens
            window.location.href = isAdmin ? '/admin' : '/dashboard';
        } catch (err) {
            console.error('Authentication error:', err);
            let errorMessage = 'An error occurred';
            
            if (err instanceof Error) {
                if (err.message === 'Failed to fetch') {
                    errorMessage = 'Cannot connect to server. Please check:\n' +
                                 '1. Your internet connection\n' +
                                 '2. The API server is running\n' +
                                 '3. CORS is enabled on the server\n' +
                                 'API URL: ' + getApiUrl('auth/login');
                } else {
                    errorMessage = err.message;
                }
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate('/');
    };

    const switchToSignup = () => {
        setActiveTab('signup');
        setError('');
        setForgotMessage('');
        setShowForgotPassword(false);
        setFormData((prev) => ({
            ...prev,
            password: ''
        }));
    };

    const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const email = forgotEmail.trim();
        if (!email) {
            setError(t('auth.enterEmail'));
            return;
        }

        setForgotLoading(true);
        setError('');
        setForgotMessage('');

        try {
            const response = await forgotPassword(email);
            setForgotMessage(response.message || t('auth.resetLinkSent'));
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(t('messages.errorOccurred'));
            }
        } finally {
            setForgotLoading(false);
        }
    };

    const renderInlineMarkdown = (text: string, keyPrefix: string) => {
        const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);

        return parts.map((part, index) => {
            const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
            if (boldMatch) {
                return <strong key={`${keyPrefix}-b-${index}`}>{boldMatch[1]}</strong>;
            }

            const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
            if (linkMatch) {
                return (
                    <a
                        key={`${keyPrefix}-a-${index}`}
                        href={linkMatch[2]}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.policyLinkInline}
                    >
                        {linkMatch[1]}
                    </a>
                );
            }

            return <React.Fragment key={`${keyPrefix}-t-${index}`}>{part}</React.Fragment>;
        });
    };

    const renderPolicyMarkdown = (content: string) => {
        const lines = content.split(/\r?\n/);
        const nodes: React.ReactNode[] = [];
        let paragraphBuffer: string[] = [];
        let listBuffer: string[] = [];
        let listType: 'ul' | 'ol' | null = null;
        let keyCounter = 0;

        const flushParagraph = () => {
            if (!paragraphBuffer.length) {
                return;
            }

            const text = paragraphBuffer.join(' ').trim();
            if (text) {
                nodes.push(
                    <p key={`p-${keyCounter++}`} className={styles.policyParagraph}>
                        {renderInlineMarkdown(text, `p-${keyCounter}`)}
                    </p>
                );
            }

            paragraphBuffer = [];
        };

        const flushList = () => {
            if (!listBuffer.length || !listType) {
                return;
            }

            if (listType === 'ul') {
                nodes.push(
                    <ul key={`ul-${keyCounter++}`} className={styles.policyList}>
                        {listBuffer.map((item, itemIndex) => (
                            <li key={`uli-${keyCounter}-${itemIndex}`}>{renderInlineMarkdown(item, `uli-${keyCounter}-${itemIndex}`)}</li>
                        ))}
                    </ul>
                );
            } else {
                nodes.push(
                    <ol key={`ol-${keyCounter++}`} className={styles.policyListOrdered}>
                        {listBuffer.map((item, itemIndex) => (
                            <li key={`oli-${keyCounter}-${itemIndex}`}>{renderInlineMarkdown(item, `oli-${keyCounter}-${itemIndex}`)}</li>
                        ))}
                    </ol>
                );
            }

            listBuffer = [];
            listType = null;
        };

        lines.forEach((line) => {
            const trimmed = line.trim();

            if (!trimmed) {
                flushParagraph();
                flushList();
                return;
            }

            const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
            if (headingMatch) {
                flushParagraph();
                flushList();

                const level = headingMatch[1].length;
                const text = headingMatch[2];

                if (level === 1) {
                    nodes.push(
                        <h2 key={`h1-${keyCounter++}`} className={styles.policyHeadingPrimary}>
                            {renderInlineMarkdown(text, `h1-${keyCounter}`)}
                        </h2>
                    );
                } else if (level === 2) {
                    nodes.push(
                        <h3 key={`h2-${keyCounter++}`} className={styles.policyHeadingSecondary}>
                            {renderInlineMarkdown(text, `h2-${keyCounter}`)}
                        </h3>
                    );
                } else {
                    nodes.push(
                        <h4 key={`h3-${keyCounter++}`} className={styles.policyHeadingTertiary}>
                            {renderInlineMarkdown(text, `h3-${keyCounter}`)}
                        </h4>
                    );
                }

                return;
            }

            const unorderedMatch = trimmed.match(/^[-*]\s+(.+)$/);
            if (unorderedMatch) {
                flushParagraph();
                if (listType && listType !== 'ul') {
                    flushList();
                }
                listType = 'ul';
                listBuffer.push(unorderedMatch[1]);
                return;
            }

            const orderedMatch = trimmed.match(/^\d+\.\s+(.+)$/);
            if (orderedMatch) {
                flushParagraph();
                if (listType && listType !== 'ol') {
                    flushList();
                }
                listType = 'ol';
                listBuffer.push(orderedMatch[1]);
                return;
            }

            if (listType) {
                flushList();
            }

            paragraphBuffer.push(trimmed);
        });

        flushParagraph();
        flushList();

        return nodes.length ? nodes : (
            <p className={styles.policyParagraph}>{content}</p>
        );
    };

    useEffect(() => {
        if (!policyModal) {
            setPolicyContent('');
            setPolicyLoading(false);
            return;
        }

        const languageCode = i18n.language.toLowerCase();
        const isEnglish = languageCode.startsWith('en');
        const fileName = policyModal === 'terms'
            ? (isEnglish ? 'easy_staff_terms.en.md' : 'easy_staff_terms.md')
            : (isEnglish ? 'easy_staff_privacy.en.md' : 'easy_staff_privacy.md');

        const fileUrl = `/docs/${fileName}`;
        const fallbackText = policyModal === 'terms'
            ? t('auth.termsPlaceholder')
            : t('auth.privacyPlaceholder');

        let cancelled = false;
        setPolicyLoading(true);

        fetch(fileUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load policy file: ${fileName}`);
                }

                return response.text();
            })
            .then((content) => {
                if (!cancelled) {
                    setPolicyContent(content);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPolicyContent(fallbackText);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPolicyLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [policyModal, i18n.language, t]);

    return (
        <div className={styles.signinPage}>
            <header className={styles.header}>
                <button className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`} onClick={handleBackClick} title="Go back" aria-label="Go back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                </button>
                <h1 className={styles.headerTitle}>{t('auth.welcomeMessage')}</h1>
            </header>

            <div className={styles.signinContainer}>
                <div className={styles.signinContent}>
                    {/* Login Section - only show when on login tab */}
                    {activeTab === 'login' && (
                        <div className={styles.signinCard}>
                            <h3 className={styles.cardTitle}>{t('auth.signIn')}</h3>
                            
                            {error && (
                                <div className={styles.errorMessage}>
                                    {error}
                                </div>
                            )}
                            
                            <form onSubmit={handleSubmit} className={styles.signinForm}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="email">{t('auth.email')}</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        placeholder={t('auth.enterEmail')}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="password">{t('auth.password')}</label>
                                    <div className={styles.passwordInputWrapper}>
                                        <input
                                            type={showLoginPassword ? 'text' : 'password'}
                                            id="password"
                                            name="password"
                                            placeholder={t('auth.enterPassword')}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            autoComplete="current-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggleBtn}
                                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                                            aria-label="Toggle password visibility"
                                        >
                                            {showLoginPassword ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                    <line x1="1" y1="1" x2="23" y2="23"></line>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" className={styles.submitButton} disabled={loading || !isLoginFormValid}>
                                    {loading ? t('common.loading') : t('auth.login')}
                                </button>
                            </form>

                            <div className={styles.forgotPasswordActions}>
                                <button
                                    type="button"
                                    className={styles.forgotPasswordLink}
                                    onClick={() => {
                                        setShowForgotPassword((prev) => !prev);
                                        setForgotEmail(formData.email);
                                        setForgotMessage('');
                                        setError('');
                                    }}
                                >
                                    {t('auth.forgotPassword')}
                                </button>
                            </div>

                            {showForgotPassword && (
                                <form onSubmit={handleForgotPasswordSubmit} className={styles.forgotPasswordPanel}>
                                    <div className={styles.formGroup}>
                                        <label htmlFor="forgot-email">{t('auth.email')}</label>
                                        <input
                                            type="email"
                                            id="forgot-email"
                                            name="forgot-email"
                                            placeholder={t('auth.enterEmail')}
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <button type="submit" className={styles.forgotPasswordButton} disabled={forgotLoading}>
                                        {forgotLoading ? t('common.loading') : t('auth.sendResetLink')}
                                    </button>

                                    {forgotMessage && <div className={styles.infoMessage}>{forgotMessage}</div>}
                                </form>
                            )}
                        </div>
                    )}

                    {/* Don't have an account section - only show when on login tab */}
                    {activeTab === 'login' && (
                        <div className={styles.signupSection}>
                            <p className={styles.signupPrompt}>
                                {t('auth.dontHaveAccount')} <button onClick={switchToSignup} className={styles.toggleLink}>{t('auth.signUpHere')}</button>
                            </p>
                        </div>
                    )}

                    {/* Signup Section */}
                    {activeTab === 'signup' && (
                        <div className={`${styles.signinCard} ${styles.signupCard}`}>
                            <h3 className={styles.cardTitle}>{t('auth.signUp')}</h3>
                            <p className={styles.requiredHint}>{t('auth.requiredFieldsHint')}</p>

                            {/* User Type Tabs */}
                            <div className={styles.userTypeTabs}>
                                <button
                                    type="button"
                                    className={`${styles.tabButton} ${signupUserType === 'staff' ? styles.active : ''}`}
                                    onClick={() => {
                                        setSignupUserType('staff');
                                        setFormData({ ...formData, role: '', phoneNumber: '', businessName: '' });
                                    }}
                                >
                                    {t('auth.lookingForJob')}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.tabButton} ${signupUserType === 'employer' ? styles.active : ''}`}
                                    onClick={() => {
                                        setSignupUserType('employer');
                                        setFormData({ ...formData, role: '', phoneNumber: '', placeOfResidence: '', acceptedTerms: false });
                                    }}
                                >
                                    {t('auth.lookingForStaff')}
                                </button>
                            </div>

                            {error && (
                                <div className={styles.errorMessage}>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className={styles.signinForm} autoComplete="off">
                                {signupUserType === 'employer' && (
                                    <div className={styles.formGroup}>
                                        <label htmlFor="businessName">{t('auth.businessName')} <span className={styles.requiredMark}>*</span></label>
                                        <input
                                            type="text"
                                            id="businessName"
                                            name="businessName"
                                            placeholder={t('auth.enterBusinessName')}
                                            value={formData.businessName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label htmlFor="signup-email">{t('auth.email')} <span className={styles.requiredMark}>*</span></label>
                                    <input
                                        type="email"
                                        id="signup-email"
                                        name="email"
                                        placeholder={t('auth.enterEmail')}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="signup-password">{t('auth.password')} <span className={styles.requiredMark}>*</span></label>
                                    <div className={styles.passwordInputWrapper}>
                                        <input
                                            type={showSignupPassword ? 'text' : 'password'}
                                            id="signup-password"
                                            name="password"
                                            placeholder={t('auth.enterPassword')}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            autoComplete="new-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className={styles.passwordToggleBtn}
                                            onClick={() => setShowSignupPassword(!showSignupPassword)}
                                            aria-label="Toggle password visibility"
                                        >
                                            {showSignupPassword ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                    <line x1="1" y1="1" x2="23" y2="23"></line>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="fullName">{t('auth.fullName')} <span className={styles.requiredMark}>*</span></label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        placeholder={t('auth.enterFullName')}
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="role">
                                        {signupUserType === 'staff' ? t('auth.jobRole') : t('auth.position')} <span className={styles.requiredMark}>*</span>
                                    </label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">{t('auth.selectRole')}</option>
                                        {roles.map((role) => (
                                            <option key={role} value={role}>
                                                {t(`roles.${role.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="phoneNumber">{t('auth.phoneNumber')} <span className={styles.requiredMark}>*</span></label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        placeholder={t('auth.enterPhoneNumber')}
                                        value={formData.phoneNumber}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="placeOfResidence">{t('auth.placeOfResidence')} <span className={styles.requiredMark}>*</span></label>
                                    <input
                                        type="text"
                                        id="placeOfResidence"
                                        name="placeOfResidence"
                                        placeholder={t('auth.enterPlaceOfResidence')}
                                        value={formData.placeOfResidence}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                                    <div className={styles.checkboxInlineRow}>
                                        <input
                                            type="checkbox"
                                            id="acceptedTerms"
                                            name="acceptedTerms"
                                            checked={formData.acceptedTerms}
                                            onChange={(e) => setFormData({ ...formData, acceptedTerms: e.target.checked })}
                                            required
                                        />
                                        <label htmlFor="acceptedTerms" className={styles.checkboxLabelInline}>
                                            {t('auth.acceptTerms')} 
                                            <button
                                                type="button"
                                                className={styles.policyLinkButton}
                                                onClick={() => setPolicyModal('terms')}
                                            >
                                                {t('auth.termsAndConditions')}
                                            </button>
                                            {' '}{t('common.and', { defaultValue: 'and' })}{' '}
                                            <button
                                                type="button"
                                                className={styles.policyLinkButton}
                                                onClick={() => setPolicyModal('privacy')}
                                            >
                                                {t('auth.privacyPolicy')}
                                            </button>
                                            <span className={styles.requiredMark}>*</span>
                                        </label>
                                    </div>
                                </div>

                                <button type="submit" className={styles.submitButton} disabled={loading || !isSignupFormValid}>
                                    {loading ? t('common.loading') : t('auth.signUp')}
                                </button>
                            </form>

                            <div className={styles.toggleMode}>
                                <p>
                                    {t('auth.alreadyHaveAccount')} <button onClick={() => setActiveTab('login')} className={styles.toggleLink}>{t('auth.signInHere')}</button>
                                </p>
                            </div>

                            {policyModal && (
                                <div className={styles.policyModalOverlay} onClick={() => setPolicyModal(null)} role="presentation">
                                    <div
                                        className={styles.policyModal}
                                        role="dialog"
                                        aria-modal="true"
                                        aria-labelledby="policy-modal-title"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className={styles.policyModalHeader}>
                                            <h4 id="policy-modal-title" className={styles.policyModalTitle}>
                                                {policyModal === 'terms'
                                                    ? t('auth.termsAndConditions')
                                                    : t('auth.privacyPolicy')}
                                            </h4>
                                            <button
                                                type="button"
                                                className={styles.policyModalClose}
                                                onClick={() => setPolicyModal(null)}
                                                aria-label={t('common.close', { defaultValue: 'Close' })}
                                            >
                                                ×
                                            </button>
                                        </div>
                                        <div className={styles.policyModalBody}>
                                            {policyLoading ? (
                                                <p>{t('common.loading')}</p>
                                            ) : (
                                                <div className={styles.policyDocumentContent}>
                                                    {renderPolicyMarkdown(policyContent)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignIn;
